/**
 * Geography > Postal Codes (WPS-222) - the postal-code -> zone crosswalk the
 * resolver reads first. Tens of thousands of rows per tenant: searchable on
 * the indexed postal code / zone name, imported in bulk, edited one row at a
 * time for corrections.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Hash, Plus, Upload } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import type { ProductFilterFacet } from '@/shared/components/ui/product-filter-menu'
import type { FormFieldMeta } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { EditFormDialog } from '@/admin/components/shared/edit-form-dialog'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { BulkImportDialog } from '@/admin/components/imports/bulk-import-dialog'
import { usePermissions } from '@/admin/hooks/use-permissions'
import {
  msas,
  postalCodes,
  useGeoMeta,
  type PostalCodeRow, useRefreshGeoData } from '@/admin/hooks/use-geo-reference'

import { CodeCell, GeographySectionShell, TextCell } from './geography-shared'
import { useCountLine, useZoneOptions, withCurrentValue, zoneLabel } from './geography-utils'
import { NONE, nullable, saveWithFieldErrors, useListState } from './section-crud'

export function PostalCodesSection() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canCreate = can('geo-zones.create')
  const canEdit = can('geo-zones.edit')
  const canDelete = can('geo-zones.delete')

  const list = useListState()

  const refreshGeoData = useRefreshGeoData()
  const { data, isLoading, isFetching, isError, refetch } = postalCodes.useList(list.params)
  const create = postalCodes.useCreate()
  const update = postalCodes.useUpdate()
  const remove = postalCodes.useDelete()
  const { data: meta } = useGeoMeta()
  const zoneOptions = useZoneOptions()
  const { data: msaData } = msas.useList({ page_size: 500 })

  const [editing, setEditing] = useState<PostalCodeRow | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleting, setDeleting] = useState<PostalCodeRow | null>(null)
  const [showImport, setShowImport] = useState(false)

  const rows = data?.results ?? []
  const countLine = useCountLine(
    data?.count,
    t('geography.postalCodes.countOne', 'postal code'),
    t('geography.postalCodes.countMany', 'postal codes'),
  )

  const countryOptions = useMemo(
    () => withCurrentValue(meta?.country_codes, editing?.country_code ?? 'US'),
    [editing?.country_code, meta?.country_codes],
  )
  const msaOptions = useMemo(
    () => [
      { value: NONE, label: t('geography.common.none', 'None') },
      ...(msaData?.results ?? []).map((m) => ({ value: m.uuid, label: `${m.title} (${m.code})` })),
    ],
    [msaData?.results, t],
  )

  const facets = useMemo<ProductFilterFacet[]>(
    () => [
      { key: 'zone', label: t('geography.postalCodes.field.zone', 'Zone'), kind: 'field', options: zoneOptions },
      {
        key: 'country_code',
        label: t('geography.postalCodes.field.country', 'Country'),
        kind: 'field',
        options: meta?.country_codes ?? [],
      },
    ],
    [meta?.country_codes, t, zoneOptions],
  )

  const fields = useMemo<FormFieldMeta[]>(
    () => [
      {
        code_name: 'postal_code',
        display_name: t('geography.postalCodes.field.postalCode', 'Postal code'),
        field_type: 'TEXT',
        required: true,
      },
      {
        code_name: 'country_code',
        display_name: t('geography.postalCodes.field.country', 'Country'),
        field_type: countryOptions.length > 1 ? 'SELECT' : 'TEXT',
        required: true,
        options: countryOptions,
      },
      {
        code_name: 'zone_uuid',
        display_name: t('geography.postalCodes.field.zone', 'Zone'),
        field_type: 'SELECT',
        required: true,
        options: zoneOptions,
        configuration_data: { searchable: true },
      },
      {
        code_name: 'msa_uuid',
        display_name: t('geography.postalCodes.field.msa', 'MSA'),
        field_type: 'SELECT',
        required: false,
        options: msaOptions,
        configuration_data: { searchable: true },
        description: t(
          'geography.postalCodes.field.msaHint',
          'Optional Metropolitan Statistical Area used as a fallback when a county is unknown.',
        ),
      },
    ],
    [countryOptions, msaOptions, t, zoneOptions],
  )

  const values = useMemo<Record<string, unknown>>(
    () =>
      editing
        ? {
            postal_code: editing.postal_code,
            country_code: editing.country_code,
            zone_uuid: editing.zone_uuid,
            msa_uuid: editing.msa_uuid ?? NONE,
          }
        : { country_code: 'US', msa_uuid: NONE },
    [editing],
  )

  const columns = useMemo<DataTableColumn<PostalCodeRow>[]>(
    () => [
      {
        id: 'postal_code',
        header: t('geography.postalCodes.column.postalCode', 'Postal code'),
        sortable: true,
        sortField: 'postal_code',
        cell: (r) => <CodeCell value={r.postal_code} />,
      },
      {
        id: 'country_code',
        header: t('geography.postalCodes.column.country', 'Country'),
        cell: (r) => <TextCell muted value={r.country_code} />,
      },
      {
        id: 'zone',
        header: t('geography.postalCodes.column.zone', 'Zone'),
        sortable: true,
        sortField: 'zone__name',
        cell: (r) => <TextCell value={zoneLabel(r.zone_name, r.zone_code)} />,
      },
      {
        id: 'msa',
        header: t('geography.postalCodes.column.msa', 'MSA'),
        cell: (r) => <TextCell muted value={r.msa_title ? `${r.msa_title} (${r.msa_code})` : '-'} />,
      },
      {
        id: 'actions',
        header: '',
        cell: (r) =>
          canEdit || canDelete ? (
            <div className="flex justify-end gap-2">
              {canEdit && (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => {
                    setEditing(r)
                    setFormOpen(true)
                  }}
                >
                  {t('geography.common.edit', 'Edit')}
                </Button>
              )}
              {canDelete && (
                <Button variant="ghost" size="xs" onClick={() => setDeleting(r)}>
                  {t('geography.common.delete', 'Delete')}
                </Button>
              )}
            </div>
          ) : null,
      },
    ],
    [canDelete, canEdit, t],
  )

  return (
    <>
      <GeographySectionShell
        testId="geography-postal-codes"
        title={t('geography.postalCodes.title', 'Postal Codes')}
        description={t(
          'geography.postalCodes.description',
          'The postal-code to zone crosswalk - the first thing an address is resolved against. Import the full crosswalk from a spreadsheet; correct individual codes here.',
        )}
        countLine={countLine}
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={t('geography.postalCodes.searchPlaceholder', 'Search postal code or zone')}
        facets={facets}
        filterValues={list.filterValues}
        onFilterValuesChange={list.setFilterValues}
        actions={
          canCreate ? (
            <>
              <Button variant="outline" size="sm" data-testid="geography-postal-import-btn" onClick={() => setShowImport(true)}>
                <Upload className="h-3.5 w-3.5" />
                {t('geography.common.import', 'Import')}
              </Button>
              <Button
                size="sm"
                data-testid="geography-postal-add-btn"
                onClick={() => {
                  setEditing(null)
                  setFormOpen(true)
                }}
              >
                <Plus className="h-3.5 w-3.5" />
                {t('geography.postalCodes.add', 'Add postal code')}
              </Button>
            </>
          ) : null
        }
      >
        <DataTable<PostalCodeRow>
          tableId="geography-postal-codes"
          columns={columns}
          data={rows}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          onRetry={() => refetch()}
          page={list.page}
          pageSize={list.pageSize}
          totalCount={data?.count ?? 0}
          onPageChange={list.setPage}
          onPageSizeChange={list.setPageSize}
          rowKey={(r) => r.uuid}
          emptyStateNode={
            <EmptyState
              icon={Hash}
              title={t('geography.postalCodes.emptyTitle', 'No postal codes')}
              description={t(
                'geography.postalCodes.emptyDescription',
                'Import the postal-code crosswalk so addresses resolve to zones.',
              )}
            />
          }
        />
      </GeographySectionShell>

      <EditFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open)
          if (!open) setEditing(null)
        }}
        title={
          editing
            ? t('geography.postalCodes.editTitle', 'Edit postal code')
            : t('geography.postalCodes.addTitle', 'Add postal code')
        }
        fields={fields}
        values={values}
        isPending={create.isPending || update.isPending}
        onSave={(v, _reason, helpers) =>
          saveWithFieldErrors(
            async () => {
              const payload = {
                postal_code: String(v.postal_code ?? '').trim(),
                country_code: String(v.country_code ?? 'US').trim(),
                zone_uuid: String(v.zone_uuid),
                msa_uuid: nullable(v.msa_uuid),
              }
              if (editing) {
                await update.mutateAsync({ uuid: editing.uuid, data: payload })
              } else {
                await create.mutateAsync(payload)
              }
            },
            helpers,
            editing
              ? t('geography.postalCodes.updated', 'Postal code updated.')
              : t('geography.postalCodes.created', 'Postal code added.'),
            t('geography.postalCodes.saveFailed', 'Could not save the postal code.'),
          )
        }
      />

      <ConfirmDeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null)
        }}
        title={t('geography.postalCodes.deleteTitle', 'Delete postal code?')}
        description={
          deleting
            ? t(
                'geography.postalCodes.deleteDescription',
                '{{code}} will no longer resolve to {{zone}}.',
                { code: deleting.postal_code, zone: deleting.zone_name },
              )
            : undefined
        }
        isPending={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return
          try {
            await remove.mutateAsync(deleting.uuid)
            toast.success(t('geography.postalCodes.deleted', 'Postal code deleted.'))
            setDeleting(null)
          } catch (err) {
            toastApiError(
              err,
              t('geography.postalCodes.deleteFailed', 'Could not delete the postal code.'),
            )
          }
        }}
      />

      <BulkImportDialog
        onComplete={refreshGeoData}
        open={showImport}
        onOpenChange={setShowImport}
        target="geo_postal_codes"
        title={t('geography.postalCodes.importTitle', 'Import postal codes')}
      />
    </>
  )
}
